<template>
  <div id="wrapper" class="natcon-app-wrapper" :style="{ marginTop: TestMode ? '40px' : '0' }">
    <!-- Test Mode Banner if active -->
    <div v-if="TestMode" class="test-mode-banner">
      <i class="fas fa-flask mr-2"></i> TEST / DEMO MODE ACTIVE
    </div>

    <!-- Mobile Backdrop Overlay when sidebar is open on mobile -->
    <transition name="fade">
      <div
        v-if="sidebarToggled"
        class="sidebar-mobile-backdrop d-md-none"
        @click="closeSidebar"
      ></div>
    </transition>

    <!-- Sidebar -->
    <sidebar-menu :toggled="sidebarToggled" @close-sidebar="closeSidebar" />
    <!-- End of Sidebar -->

    <!-- Content Wrapper -->
    <div id="content-wrapper" class="natcon-content-wrapper" :class="{ 'sidebar-collapsed': sidebarToggled }">
      <!-- Main Content -->
      <div id="content" class="natcon-main-content">
        <!-- Topbar -->
        <top-bar-menu @toggle-sidebar="toggleSidebar" />
        <!-- End of Topbar -->

        <!-- Page Content Slot with Transition -->
        <main class="page-body-container">
          <transition name="page-fade" mode="out-in">
            <slot />
          </transition>
        </main>
      </div>
      <!-- End of Main Content -->

      <!-- Footer -->
      <footer-content />
      <!-- End of Footer -->
    </div>
    <!-- End of Content Wrapper -->
  </div>
</template>

<script>
import NavMenu from "@/components/NavMenu.vue";
import TopBarMenu from "@/components/TopBarMenu.vue";
import SidebarMenu from "@/components/SidebarMenu.vue";
import FooterContent from "@/components/FooterContent.vue";
import companyConfig from "@/company_config.json";

export default {
  name: "DefaultLayout",
  components: {
    NavMenu,
    TopBarMenu,
    SidebarMenu,
    FooterContent,
  },
  data() {
    return {
      TestMode: companyConfig.TestMode,
      sidebarToggled: false,
    };
  },
  methods: {
    toggleSidebar() {
      this.sidebarToggled = !this.sidebarToggled;
    },
    closeSidebar() {
      this.sidebarToggled = false;
    },
  },
  watch: {
    $route() {
      if (window.innerWidth <= 768) {
        this.sidebarToggled = false;
      }
    },
  },
};
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=Outfit:wght@400;500;600;700;800&display=swap');

:root {
  --jci-navy: #3A67B1;
  --jci-dark-navy: #0A0F29;
  --jci-navy-gradient: linear-gradient(135deg, #0A0F29 0%, #3A67B1 50%, #0097D7 100%);
  --jci-aqua: #0097D7;
  --jci-gold: #EDBE38;
  --jci-seafoam: #56BDA3;
  --jci-text: #414042;
  --jci-bg-slate: #FAFAFC;
  --jci-surface: #FFFFFF;
  --jci-border: #E2E8F0;
  --jci-glass: rgba(255, 255, 255, 0.85);
  --jci-shadow-sm: 0 2px 8px rgba(10, 15, 41, 0.04);
  --jci-shadow-md: 0 8px 24px rgba(10, 15, 41, 0.08);
  --jci-shadow-lg: 0 16px 40px rgba(10, 15, 41, 0.12);
  --jci-radius-sm: 8px;
  --jci-radius-md: 14px;
  --jci-radius-lg: 20px;
}

body {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  background-color: var(--jci-bg-slate);
  color: var(--jci-text);
  margin: 0;
  padding: 0;
  -webkit-font-smoothing: antialiased;
}

/* Reset Vuetify / Bootstrap container constraints */
.container,
.container-fluid,
.container--fluid {
  max-width: 100% !important;
  width: 100% !important;
}

.natcon-app-wrapper {
  display: flex;
  min-height: 100vh;
  width: 100%;
  background-color: var(--jci-bg-slate);
  position: relative;
}

.test-mode-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 40px;
  background: linear-gradient(90deg, #EDBE38, #F59E0B);
  color: #0A0F29;
  font-weight: 700;
  font-size: 0.85rem;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.natcon-content-wrapper {
  margin-left: 270px;
  width: calc(100% - 270px);
  min-height: 100vh;
  background-color: var(--jci-bg-slate);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  &.sidebar-collapsed {
    margin-left: 90px;
    width: calc(100% - 90px);
  }
}

.natcon-main-content {
  flex: 1 1 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.page-body-container {
  padding: 1.5rem 2rem 2.5rem;
  flex: 1 1 auto;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

/* Page Transition */
.page-fade-enter-active,
.page-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.page-fade-enter {
  opacity: 0;
  transform: translateY(8px);
}

.page-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.sidebar-mobile-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(10, 15, 41, 0.6);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 1040;
  cursor: pointer;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .natcon-content-wrapper {
    margin-left: 0 !important;
    width: 100% !important;
  }
  .page-body-container {
    padding: 1rem 1rem 2rem;
  }
}
</style>
